import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';
import * as yaml from 'js-yaml';
import { isNewerVersion } from './utils.js';

// Path must match the destination layout written by `.github/workflows/sync-public-skill.yml`.
export const DEFAULT_SKILL_RAW_URL =
  'https://raw.githubusercontent.com/wayai-pro/wayai-skill/main/skills/wayai/SKILL.md';

// The harness skill directories WayAI recognizes — the set the external
// `npx skills` installer fans the PUBLIC skill across, and the set the private
// `wayai admin skill install` writes the operator skill into. Single source so
// public-skill version detection and admin-skill install never drift apart. A
// lagging subset of the ~50 harnesses `npx skills` knows; extend here as we
// adopt more (e.g. `.cursor`).
export const HARNESS_SKILL_DIRS = ['.claude', '.opencode', '.agents'] as const;

// The skill manifest filename — the file we install, detect, and read the version from.
export const SKILL_FILENAME = 'SKILL.md';

/** SKILL.md paths for a given skill folder across every recognized harness dir. */
export function skillInstallPaths(skillName: string): string[] {
  return HARNESS_SKILL_DIRS.map((dir) => `${dir}/skills/${skillName}/${SKILL_FILENAME}`);
}

export const SKILL_INSTALL_PATHS = skillInstallPaths('wayai');
// Private operator skill installed by `wayai admin skill install` (platform-admin gated).
export const ADMIN_SKILL_NAME = 'wayai-admin';
export const ADMIN_SKILL_INSTALL_PATHS = skillInstallPaths(ADMIN_SKILL_NAME);

export interface InstalledSkill {
  path: string;
  version: string | null;
}

export function parseFrontmatterVersion(content: string): string | null {
  const match = /^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/.exec(content);
  if (!match) return null;
  let parsed: unknown;
  try {
    parsed = yaml.load(match[1]);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const v = (parsed as Record<string, unknown>).version;
  return typeof v === 'string' ? v : null;
}

export function findInstalledSkills(
  projectRoot: string,
  paths: readonly string[] = SKILL_INSTALL_PATHS,
): InstalledSkill[] {
  const found: InstalledSkill[] = [];
  for (const rel of paths) {
    const path = join(projectRoot, rel);
    if (!existsSync(path)) continue;
    let version: string | null = null;
    try {
      version = parseFrontmatterVersion(readFileSync(path, 'utf-8'));
    } catch {
      // Unreadable
    }
    found.push({ path, version });
  }
  return found;
}

export function lowestInstalledVersion(installs: InstalledSkill[]): string | null {
  const versions = installs
    .map((i) => i.version)
    .filter((v): v is string => v !== null);
  if (versions.length === 0) return null;
  return versions.reduce((lowest, v) => (isNewerVersion(lowest, v) ? v : lowest));
}
